import React, { FunctionComponent } from "react";
import { NodeProps } from "reactflow";
import { BasicNodeLayout, DocumentSchemaSource, NodeSize, NodeType, NodeTypeCategory } from "../Base";
import { NodeConfigPropertyType } from "../../FlowDocument/PropertyTypeDefinitions";
import { DataType, primitiveListTypes, primitiveTypes } from "../TypeDefinitions";

const plexAgent = "Plex Agent";
const plexAgentId = "plexAgentId";
const agentName = "agentName";
const outputPropertyName = "output";
const outputSchemaNameProperty = "outputSchemaName";
const outputSchemaProperty = "outputSchemaId";
const dataTypePropertyName = "dataType";

globalThis.nodeTypeDefinitions.setDefinition({
  nodeType: NodeType.callPlexAgent,
  category: NodeTypeCategory.ai,
  featureToggle: "feat-core-3999-flows-plex-agent-node",
  label: plexAgent,
  size: NodeSize.Small,
  controlInputs: [{ name: "call" }],
  controlOutputs: [{ name: "completed" }],
  dataInputs: [
    { name: "instructions", type: DataType.STRING, hideLabel: false, required: true },
    { name: "inputData", type: DataType.STRING, hideLabel: false, required: false }
  ],
  documentDataInputListName: "inputs",
  // The success output's type is changed client-side via the Output Type picker below
  // (dataProperties: ["results"]), so changing it never triggers a metadata round-trip. The
  // server metadata still supplies the default results output and the problemDetails output.
  dataOutputs: [],
  documentDataOutputList: "results",
  nodeConfigProperties: [
    {
      name: plexAgentId,
      label: "Agent",
      propertyType: NodeConfigPropertyType.PlexAgent,
      schemaSourceSystemType: DocumentSchemaSource.plexAgent,
      schemaSystem: { createIo: true }
    },
    {
      name: agentName,
      propertyType: NodeConfigPropertyType.NoInput
    },
    {
      name: dataTypePropertyName,
      label: "Output Type",
      propertyType: NodeConfigPropertyType.DataType,
      clearPropertiesOnChange: [outputSchemaProperty],
      options: {
        dataTypes: primitiveTypes
          .concat([DataType.OBJECT])
          .concat(primitiveListTypes)
          .concat([DataType.OBJECTLIST]),
        dataProperties: [outputPropertyName]
      },
      schemaSourceSystemType: DocumentSchemaSource.schemaPicker
    },
    {
      name: outputSchemaProperty,
      label: "Output Object",
      propertyType: NodeConfigPropertyType.Schema,
      showOnPropertyValues: {
        propertyName: dataTypePropertyName,
        propertyValues: [DataType.OBJECT, DataType.OBJECTLIST]
      },
      options: {
        changeSchemaOnConnection: true,
        excludeReferences: true,
        displayProperty: outputSchemaNameProperty,
        dataProperties: [outputPropertyName]
      },
      schemaSourceSystemType: DocumentSchemaSource.schemaPicker
    },
    {
      name: outputSchemaNameProperty,
      propertyType: NodeConfigPropertyType.NoInput
    },
    {
      name: "inputs",
      sectionName: "Input",
      propertyType: NodeConfigPropertyType.EnabledInputs
    },
    {
      name: outputPropertyName,
      sectionName: "results",
      propertyType: NodeConfigPropertyType.EnabledOutputs
    }
  ],
  skipEvaluate: true,
  evaluate: () => {
    return { output: {}, activeControlHandleIds: [] };
  }
});

export const CallPlexAgentNode: FunctionComponent<NodeProps> = ({ id, data }) => {
  const label = data.nodeProperties[agentName] ?? plexAgent;
  data.nodeProperties.label = label;
  return <BasicNodeLayout id={id} data={data} label={label} />;
};
